import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

export default function LocationsTab({ user, centers, onRefresh }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form, setForm] = useState({
    name: '', address: '', city: '', state: 'MD', zip: '', phone: '',
    isActive: true,
    hours: DAYS.reduce((a, d) => ({ ...a, [d]: { open: true, from: '08:00', to: '17:00' } }), {}),
  });

  useEffect(() => { loadLocations(); }, [user.uid]);

  const loadLocations = async () => {
    try {
      const snap = await getDocs(collection(db, 'serviceCenters', user.uid, 'locations'));
      setLocations(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', address: '', city: '', state: 'MD', zip: '', phone: '', isActive: true, hours: DAYS.reduce((a, d) => ({ ...a, [d]: { open: true, from: '08:00', to: '17:00' } }), {}) });
    setShowForm(true);
  };

  const openEdit = (loc) => {
    setEditing(loc);
    setForm({ name: loc.name || '', address: loc.address || '', city: loc.city || '', state: loc.state || 'MD', zip: loc.zip || '', phone: loc.phone || '', isActive: loc.isActive !== false, hours: loc.hours || DAYS.reduce((a, d) => ({ ...a, [d]: { open: true, from: '08:00', to: '17:00' } }), {}) });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.address) { alert('Address is required.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, updatedAt: serverTimestamp() };
      if (editing) {
        await updateDoc(doc(db, 'serviceCenters', user.uid, 'locations', editing.id), payload);
      } else {
        await addDoc(collection(db, 'serviceCenters', user.uid, 'locations'), { ...payload, createdAt: serverTimestamp() });
      }
      setShowForm(false);
      loadLocations();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this location?')) return;
    await deleteDoc(doc(db, 'serviceCenters', user.uid, 'locations', id));
    loadLocations();
  };

  const iS = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, color: '#111827', background: '#f9fafb', outline: 'none', boxSizing: 'border-box' };
  const lS = { display: 'block', fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 };

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 900 }}>Locations</h1>
          <p style={{ color: '#6b7280', marginTop: 4 }}>Manage your service center locations</p>
        </div>
        <button onClick={openAdd} style={{ background: '#F97316', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>+ Add Location</button>
      </div>

      {loading ? <p style={{ color: '#6b7280' }}>Loading...</p> : locations.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 16, padding: 48, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📍</div>
          <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>No locations yet</h3>
          <p style={{ color: '#6b7280', marginBottom: 20 }}>Add your service center location so CocoWash drivers know where to bring vehicles</p>
          <button onClick={openAdd} style={{ background: '#F97316', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 24px', fontWeight: 700, cursor: 'pointer' }}>Add First Location</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {locations.map(loc => (
            <div key={loc.id} style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 800 }}>{loc.name || 'Main Location'}</h3>
                  <span style={{ fontSize: 11, fontWeight: 700, color: loc.isActive ? '#16a34a' : '#dc2626', background: loc.isActive ? '#f0fdf4' : '#fef2f2', padding: '2px 8px', borderRadius: 20 }}>{loc.isActive ? 'Active' : 'Inactive'}</span>
                </div>
                <p style={{ fontSize: 13, color: '#6b7280' }}>{loc.address}{loc.city ? ', ' + loc.city : ''}{loc.state ? ', ' + loc.state : ''} {loc.zip}</p>
                {loc.phone && <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{loc.phone}</p>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => openEdit(loc)} style={{ padding: '8px 16px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#3b82f6' }}>Edit</button>
                <button onClick={() => remove(loc.id)} style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#dc2626', fontWeight: 700 }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: '100%', maxWidth: 520, maxHeight: '85vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: 20, fontWeight: 900, marginBottom: 24 }}>{editing ? 'Edit Location' : 'Add Location'}</h2>
            <label style={lS}>Location Name (optional)</label>
            <input style={{ ...iS, marginBottom: 14 }} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Main Branch, North Location" />
            <label style={lS}>Street Address *</label>
            <input style={{ ...iS, marginBottom: 14 }} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="123 Main Street" />
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div><label style={lS}>City</label><input style={iS} value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Gaithersburg" /></div>
              <div><label style={lS}>State</label><input style={iS} value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} /></div>
              <div><label style={lS}>ZIP</label><input style={iS} value={form.zip} onChange={e => setForm(f => ({ ...f, zip: e.target.value }))} /></div>
            </div>
            <label style={lS}>Phone Number</label>
            <input style={{ ...iS, marginBottom: 14 }} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(301) 555-0100" />
            <label style={lS}>Hours</label>
            {DAYS.map(day => (
              <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ width: 90, fontSize: 13, fontWeight: 600 }}>{day}</div>
                <input type="checkbox" checked={form.hours[day]?.open ?? true} onChange={e => setForm(f => ({ ...f, hours: { ...f.hours, [day]: { ...f.hours[day], open: e.target.checked } } }))} style={{ accentColor: '#F97316' }} />
                {form.hours[day]?.open ? (
                  <>
                    <input type="time" value={form.hours[day]?.from || '08:00'} onChange={e => setForm(f => ({ ...f, hours: { ...f.hours, [day]: { ...f.hours[day], from: e.target.value } } }))} style={{ ...iS, width: 110 }} />
                    <span style={{ color: '#6b7280', fontSize: 13 }}>to</span>
                    <input type="time" value={form.hours[day]?.to || '17:00'} onChange={e => setForm(f => ({ ...f, hours: { ...f.hours, [day]: { ...f.hours[day], to: e.target.value } } }))} style={{ ...iS, width: 110 }} />
                  </>
                ) : <span style={{ fontSize: 13, color: '#9ca3af' }}>Closed</span>}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button onClick={() => setShowForm(false)} style={{ flex: 1, padding: '12px', background: '#f3f4f6', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={save} disabled={saving} style={{ flex: 1, padding: '12px', background: '#F97316', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save Location'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}